import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";
import { useCanViewApiAudit } from "../../hooks/useCanViewApiAudit";
import {
  buildAiAuditCopyBundle,
  formatAiAuditJson,
  getAiAuditCategoryLabel,
  looksLikeAiAgentLogText,
  resolveMessageAiAudit,
} from "../../utils/aiAgentAuditDisplay";

async function copyText(text) {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
}

const JsonBlock = ({ value, label }) => (
  <Box>
    {label ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {label}
      </Typography>
    ) : null}
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        maxHeight: 360,
        overflow: "auto",
        fontSize: "0.7rem",
        lineHeight: 1.45,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        bgcolor: "action.hover",
        borderRadius: 1,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {formatAiAuditJson(value) || "—"}
    </Box>
  </Box>
);

const parseJsonMaybe = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const AiAgentAuditPanel = ({ message, compact = false, defaultOpen = false }) => {
  const theme = useTheme();
  const { t } = useTranslation("app");
  const canView = useCanViewApiAudit();
  const audit = useMemo(() => resolveMessageAiAudit(message), [message]);
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState("");

  const looksLikeAi =
    Boolean(audit) ||
    looksLikeAiAgentLogText(message?.textPlain || message?.text || "") ||
    Boolean(message?.aiAudit) ||
    /ai_agent_audit|xpro_ai_audit/i.test(String(message?.json_whatsapp || ""));

  // Só usa JSON bruto quando a mensagem já é de auditoria de IA.
  // Qualquer json_whatsapp (ex.: xpro_log de status) não deve abrir este painel —
  // senão master vê "Detalhes de IA" duplicando o conteúdo técnico.
  const rawFallback = useMemo(() => {
    if (audit || !looksLikeAi) return null;
    const fromWa = parseJsonMaybe(message?.json_whatsapp);
    if (fromWa && (fromWa.xpro_ai_audit || fromWa.kind === "ai_agent_audit")) {
      return fromWa;
    }
    const fromAck = parseJsonMaybe(message?.json_status_message_ack);
    if (fromAck && (fromAck.xpro_ai_audit || fromAck.kind === "ai_agent_audit")) {
      return fromAck;
    }
    return null;
  }, [audit, looksLikeAi, message]);

  if (!canView || (!looksLikeAi && !audit)) return null;

  const isRule = audit?.category === "internal_rule";
  const categoryLabel = audit
    ? getAiAuditCategoryLabel(audit.category)
    : t("chat.aiAgentAudit.genericCategory", "IA");
  const duration =
    audit?.durationMs != null && Number.isFinite(Number(audit.durationMs))
      ? `${audit.durationMs}ms`
      : null;

  const handleCopy = async (kind) => {
    let text = "";
    if (kind === "request") text = formatAiAuditJson(audit?.request);
    else if (kind === "response") text = formatAiAuditJson(audit?.response);
    else if (kind === "rules") text = formatAiAuditJson(audit?.rules);
    else if (kind === "context") text = formatAiAuditJson(audit?.context);
    else if (kind === "raw") text = formatAiAuditJson(rawFallback);
    else text = audit ? buildAiAuditCopyBundle(audit) : formatAiAuditJson(rawFallback);
    await copyText(text);
    setCopied(kind);
    setTimeout(() => setCopied(""), 2000);
  };

  const showStructured = Boolean(audit);
  const showRawOnly = !showStructured && Boolean(rawFallback);

  return (
    <Box sx={{ mt: compact ? 0.5 : 0.75, width: "100%", textAlign: "left" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.5,
          justifyContent: "center",
        }}
      >
        <Button
          size="small"
          variant={open ? "outlined" : "contained"}
          color="secondary"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          startIcon={<PsychologyOutlinedIcon sx={{ fontSize: 16 }} />}
          endIcon={open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontSize: "0.72rem",
            minWidth: 0,
            px: 1,
            py: 0.25,
            boxShadow: "none",
          }}
        >
          {t("chat.aiAgentAudit.technicalDetails", "Detalhes de IA")}
        </Button>
        <Chip size="small" label={categoryLabel} sx={{ height: 20, fontSize: "0.65rem" }} />
        {audit?.operation || audit?.featureKind ? (
          <Chip
            size="small"
            variant="outlined"
            label={audit.operation || audit.featureKind}
            sx={{ height: 20, fontSize: "0.65rem" }}
          />
        ) : null}
        {audit?.model ? (
          <Chip size="small" variant="outlined" label={audit.model} sx={{ height: 20, fontSize: "0.65rem" }} />
        ) : null}
        {audit?.error ? (
          <Chip size="small" color="error" label="erro" sx={{ height: 20, fontSize: "0.65rem" }} />
        ) : null}
        {duration ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
            {duration}
          </Typography>
        ) : null}
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            mt: 0.75,
            p: 1,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            textAlign: "left",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {!showStructured && !showRawOnly ? (
            <Typography variant="caption" color="text.secondary">
              {t(
                "chat.aiAgentAudit.unavailable",
                "Não há payload estruturado (request/response) para este log de IA."
              )}
            </Typography>
          ) : (
            <>
              <Box sx={{ display: "flex", gap: 0.5, mb: 0.75, flexWrap: "wrap" }}>
                {showStructured && isRule ? (
                  <>
                    <Button size="small" variant="outlined" onClick={() => handleCopy("rules")} sx={{ fontSize: "0.68rem" }}>
                      {t("chat.aiAgentAudit.copyRules", "Copiar regras")}
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => handleCopy("context")} sx={{ fontSize: "0.68rem" }}>
                      {t("chat.aiAgentAudit.copyContext", "Copiar contexto")}
                    </Button>
                  </>
                ) : null}
                {showStructured && !isRule ? (
                  <>
                    <Button size="small" variant="outlined" onClick={() => handleCopy("request")} sx={{ fontSize: "0.68rem" }}>
                      {t("chat.aiAgentAudit.copyRequest", "Copiar request")}
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => handleCopy("response")} sx={{ fontSize: "0.68rem" }}>
                      {t("chat.aiAgentAudit.copyResponse", "Copiar response")}
                    </Button>
                  </>
                ) : null}
                {showRawOnly ? (
                  <Button size="small" variant="outlined" onClick={() => handleCopy("raw")} sx={{ fontSize: "0.68rem" }}>
                    {t("chat.aiAgentAudit.copyRaw", "Copiar JSON bruto")}
                  </Button>
                ) : null}
                <Button size="small" variant="contained" onClick={() => handleCopy("all")} sx={{ fontSize: "0.68rem" }}>
                  {t("chat.aiAgentAudit.copyAll", "Copiar tudo")}
                </Button>
                {copied ? (
                  <Typography variant="caption" color="success.main" sx={{ alignSelf: "center" }}>
                    {t("chat.aiAgentAudit.copied", "Copiado!")}
                  </Typography>
                ) : null}
              </Box>

              {showStructured && isRule ? (
                <>
                  <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{ minHeight: 32, mb: 0.5, "& .MuiTab-root": { minHeight: 32, py: 0.25, fontSize: "0.72rem" } }}
                  >
                    <Tab label={t("chat.aiAgentAudit.rulesTab", "Regras")} />
                    <Tab label={t("chat.aiAgentAudit.contextTab", "Contexto")} />
                  </Tabs>
                  {tab === 0 ? <JsonBlock value={audit.rules} /> : null}
                  {tab === 1 ? <JsonBlock value={audit.context} /> : null}
                </>
              ) : null}

              {showStructured && !isRule ? (
                <>
                  <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{ minHeight: 32, mb: 0.5, "& .MuiTab-root": { minHeight: 32, py: 0.25, fontSize: "0.72rem" } }}
                  >
                    <Tab label={t("chat.aiAgentAudit.requestTab", "Request")} />
                    <Tab label={t("chat.aiAgentAudit.responseTab", "Response")} />
                    {audit.usage ? <Tab label={t("chat.aiAgentAudit.usageTab", "Usage")} /> : null}
                    {rawFallback ? <Tab label={t("chat.aiAgentAudit.rawTab", "JSON bruto")} /> : null}
                  </Tabs>
                  {tab === 0 ? <JsonBlock value={audit.request} /> : null}
                  {tab === 1 ? <JsonBlock value={audit.response} /> : null}
                  {audit.usage && tab === 2 ? <JsonBlock value={audit.usage} /> : null}
                  {rawFallback && tab === (audit.usage ? 3 : 2) ? <JsonBlock value={rawFallback} /> : null}
                </>
              ) : null}

              {showRawOnly ? (
                <JsonBlock
                  value={rawFallback}
                  label={t("chat.aiAgentAudit.rawPayload", "Payload armazenado")}
                />
              ) : null}

              {audit?.error ? (
                <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 0.75 }}>
                  {audit.error}
                </Typography>
              ) : null}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AiAgentAuditPanel;
